// AuthLangSwitch — language picker for the unauthenticated login / register
// pages (LEA-339).
//
// Om feedback (2026-06-18): offer ALL supported languages (not just DE/EN) and
// use a dropdown so it doesn't crowd the carousel. The language list mirrors
// Account.jsx's LANG_OPTIONS so the pre-login picker and the in-app profile
// picker stay in sync; all 7 locales ship full auth.* translations.
//
// Mechanics: a selection persists. LangStore.set() writes
// localStorage['ls_lang'], fires the best-effort server PATCH
// (window.__saveLanguage), and notifies every useLang() subscriber so the auth
// copy re-renders. Persisting is intended here — the user is actively choosing
// — unlike the automatic English auth-page fallback in main.tsx, which sets the
// language display-only ({ persist: false }). Pre-login the server PATCH simply
// fails (no session) and is swallowed; the chosen language still rides into the
// new account because RegisterForm sends LangStore's language on signup and the
// OAuth path reads LangStore.get() (see lib/auth.ts).

import { type Lang, LangStore, isSupportedLang, useLang } from '@cortex/i18n';
import { ChevronDown, Globe } from 'lucide-react';

// Native-name labels, mirroring apps/web/src/pages/Account.jsx (LANG_OPTIONS).
const LANG_OPTIONS: Record<Lang, string> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
};

export default function AuthLangSwitch() {
  const lang = useLang();
  return (
    <div className="relative inline-flex items-center">
      <Globe
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
      />
      <select
        value={lang}
        onChange={(e) => {
          const next = e.target.value;
          if (!isSupportedLang(next)) return;
          // Explicit choice → persist (ls_lang + server PATCH) and re-render.
          LangStore.set(next);
          if (window.__cortexUser) window.__cortexUser.language = next;
        }}
        aria-label="Language"
        className="h-[40px] appearance-none rounded-lg border border-[rgba(52,101,227,0.16)] bg-white pl-9 pr-9 text-[14px] font-medium text-ink cursor-pointer shadow-sm transition-colors hover:border-[rgba(52,101,227,0.4)] focus:border-[#3465E3] focus:outline-none"
      >
        {(Object.keys(LANG_OPTIONS) as Lang[]).map((code) => (
          <option key={code} value={code}>
            {LANG_OPTIONS[code]}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
      />
    </div>
  );
}
